import axios from 'axios';

export interface Role {
  id: number;
  name: string;
  description?: string;
  permissions?: string[];
}

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  active: boolean;
  last_login?: Date;
  created_at: Date;
  role?: Role;
}

export interface CreateUserRequest {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  role_id: number;
}

export interface UpdateUserRequest {
  email?: string;
  first_name?: string;
  last_name?: string;
  role_id?: number;
  active?: boolean;
}

export interface ChangePasswordRequest {
  password: string;
}

class UserService {
  async getAllUsers(): Promise<User[]> {
    const response = await axios.get('/users');
    return response.data.data;
  }

  async createUser(userData: CreateUserRequest): Promise<User> {
    const response = await axios.post('/users', userData);
    return response.data.data;
  }

  async updateUser(id: number, userData: UpdateUserRequest): Promise<User> {
    const response = await axios.put(`/users/${id}`, userData);
    return response.data.data;
  }

  async deleteUser(id: number): Promise<void> {
    await axios.delete(`/users/${id}`);
  }

  async changeUserPassword(id: number, passwordData: ChangePasswordRequest): Promise<void> {
    await axios.patch(`/users/${id}/password`, passwordData);
  }

  async getRoles(): Promise<Role[]> {
    const response = await axios.get('/users/roles');
    return response.data.data;
  }

  // Utility methods
  getUserFullName(user: User): string {
    return `${user.first_name} ${user.last_name}`;
  }

  formatLastLogin(user: User): string {
    if (!user.last_login) return 'Nunca';
    
    const lastLogin = new Date(user.last_login);
    const now = new Date();
    const diffMs = now.getTime() - lastLogin.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Hoy';
    } else if (diffDays === 1) {
      return 'Ayer';
    } else if (diffDays < 7) {
      return `Hace ${diffDays} días`;
    } else {
      return lastLogin.toLocaleDateString('es-ES');
    }
  }

  getUserStatusText(user: User): string {
    return user.active ? 'Activo' : 'Inactivo';
  }

  getUserStatusColor(user: User): string {
    return user.active ? 'text-green-600' : 'text-red-600';
  }
}

export const userService = new UserService();
export default userService;